import React, { useCallback, useContext, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import apiClient, { get } from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import Modal from '../../components/common/Modal.jsx';
import FiltroFechas from '../finanzas-cxp/FiltroFechas.jsx';
import { formatMoney, formatDate, today, daysAgo, useLocalTable } from '../finanzas-cxp/shared.js';
import { exportTablePdf, buildTablePdf, exportTableExcel } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';

/**
 * PorRetirar (reparto de utilidad) -- réplica de `ListadoPorRetirar.jsx` +
 * `PorRetirarCreate.jsx` (V1). Listado: filtros Fecha Inicio/Fin, buscador, PDF/Excel,
 * tabla Numero/Fecha/Observaciones/Importe (SIN botones por fila: V1 no tiene
 * editar/cancelar aquí) y fila TOTAL. Alta: Fecha, Total, Observaciones (default "NA")
 * y tabla Socio / Participacion / Por Retirar calculada automáticamente
 * (`participacion * total / 100`) + fila Total.
 *
 * `POST /por-retirar` (`crearReparto`) recibe la cabecera + el arreglo completo de
 * distribuciones en una sola transacción (V1 hacía un POST + N POST sueltos).
 */
function PorRetirarForm({ open, onClose, socios, onSubmit }) {
  const [fecha, setFecha] = useState(today());
  const [total, setTotal] = useState(0);
  const [observaciones, setObservaciones] = useState('NA');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { if (open) { setFecha(today()); setTotal(0); setObservaciones('NA'); } }, [open]);

  // Importe de cada socio = participacion * total / 100 (V1 `jalaTotal`); al último
  // socio se le asigna el remanente para que la suma cuadre al centavo con el total.
  const reparto = (() => {
    const t = Number(total) || 0;
    let acumulado = 0;
    return socios.map((s, i) => {
      let importe = Math.round(((Number(s.participacion) || 0) * t) / 100 * 100) / 100;
      if (i === socios.length - 1 && Math.abs(socios.reduce((a, x) => a + (Number(x.participacion) || 0), 0) - 100) < 1e-9) {
        importe = Math.round((t - acumulado) * 100) / 100;
      }
      acumulado += importe;
      return { ...s, importe };
    });
  })();

  async function handleSubmit(e) {
    e.preventDefault();
    const r = await Swal.fire({
      title: 'Estas seguro?', text: 'Se registrará el retiro', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Si, Retirar!',
    });
    if (!r.isConfirmed) return;
    setGuardando(true);
    try {
      await onSubmit({
        fecha, total: Number(total) || 0, observaciones,
        socios: reparto.map((s) => ({ socio: s._id, importe: s.importe, participacion: Number(s.participacion) || 0 })),
      });
      Swal.fire('Good job!', 'Creado con exito', 'success');
      onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', footer: err.message });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      open={open} onClose={onClose} title="Captura un Retiro" width="max-w-2xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-outline">Regresar</button>
          <button type="submit" form="form-reparto" disabled={guardando} className="btn-primary">Registrar</button>
        </>
      )}
    >
      <form id="form-reparto" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Fecha</label>
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required className="input-base" />
          </div>
          <div>
            <label className="label">Total</label>
            <input type="number" min="0" step="any" value={total} onChange={(e) => setTotal(e.target.value)} className="input-base" />
          </div>
        </div>
        <div>
          <label className="label">Observaciones</label>
          <input type="text" required value={observaciones} onChange={(e) => setObservaciones(e.target.value)} className="input-base" />
        </div>
        <div className="card overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Socio</th>
                <th className="px-3 py-2 text-left">Participacion</th>
                <th className="px-3 py-2 text-left">Por Retirar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {reparto.map((s) => (
                <tr key={s._id}>
                  <td className="px-3 py-2">{s.name}</td>
                  <td className="px-3 py-2">{s.participacion}%</td>
                  <td className="px-3 py-2">{`$${new Intl.NumberFormat('en-US').format(s.importe)}`}</td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2">{`$${new Intl.NumberFormat('en-US').format(Number(total) || 0)}`}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </form>
    </Modal>
  );
}

export default function PorRetirarPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso('por-retirar:ver');
  const puedeCrear = hasPermiso('por-retirar:crear');

  const [filas, setFilas] = useState([]);
  const [socios, setSocios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [fechaInicio, setFechaInicio] = useState(daysAgo(30));
  const [fechaFin, setFechaFin] = useState(today());

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [data, sociosData] = await Promise.all([
        get(`/por-retirar/fechas/${fechaInicio}/${fechaFin}`),
        get('/socios'),
      ]);
      setSocios(sociosData || []);
      setFilas((data || []).filter((r) => r.is_active !== 'No').map((r) => ({
        _id: r._id, numero: r.idPorRetirar, fecha: r.fecha, observaciones: r.observaciones || '', importe: r.total,
      })));
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [fechaInicio, fechaFin]);

  useEffect(() => { if (puedeVer) cargar(); }, [cargar, puedeVer]);

  async function guardar(payload) {
    await apiClient.post('/por-retirar', payload);
    await cargar();
  }

  const { search, setSearch, page, setPage, sortBy, sortOrder, ordenarPor, pageRows, filtradas, pagination } = useLocalTable(filas, {
    searchFields: ['observaciones', 'importe', 'numero'], defaultSortBy: 'numero', defaultSortOrder: 'desc', perPage: 50,
  });

  const columnasPdf = [
    { key: 'numero', label: 'Numero' },
    { key: 'fecha', label: 'Fecha' },
    { key: 'observaciones', label: 'Observaciones' },
    { key: 'importe', label: 'Importe', render: (r) => formatMoney(r.importe) },
  ];
  const columnasExcel = columnasPdf.map((c) => (c.key === 'importe' ? { key: 'importe', label: 'Importe' } : c));
  const totalExport = filtradas.reduce((a, r) => a + (r.importe || 0), 0);
  const opcionesPdf = () => ({ fileName: 'Retiros', subtitulo: `${fechaInicio} - ${fechaFin}`, totalCols: { observaciones: 'Total', importe: `$${new Intl.NumberFormat('en-US').format(totalExport)}` } });

  const columnas = [
    { key: 'numero', label: 'Numero', sortable: true },
    { key: 'fecha', label: 'Fecha', sortable: true, render: (r) => formatDate(r.fecha) },
    { key: 'observaciones', label: 'Observaciones', sortable: true },
    { key: 'importe', label: 'Importe', sortable: true, render: (r) => formatMoney(r.importe) },
  ];

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Reporte de Retiros</h1>
        {puedeCrear && (
          <button onClick={() => setFormOpen(true)} className="btn-primary">Nuevo Retiro</button>
        )}
      </div>

      <FiltroFechas inicio={fechaInicio} fin={fechaFin} onInicio={setFechaInicio} onFin={setFechaFin} />

      <div className="flex items-center gap-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="input-base w-64" />
        <button type="button" className="btn-outline text-xs px-3 py-1.5"
          onClick={() => exportTablePdf('Listado Abonos', columnasPdf, filtradas, opcionesPdf())}>PDF</button>
        <button type="button" className="btn-outline text-xs px-3 py-1.5" onClick={() => setMailOpen(true)}>eMail</button>
        <button type="button" className="btn-outline text-xs px-3 py-1.5"
          onClick={() => exportTableExcel('ListadoPorRetirar', columnasExcel, filtradas, { fileName: 'ListadoPorRetirar', sheetName: 'ListadoPorRetirar' })}>Excel</button>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <DataTable columns={columnas} data={pageRows} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor}
        footer={{ observaciones: 'TOTAL', importe: formatMoney(pageRows.reduce((a, r) => a + (r.importe || 0), 0)) }} />
      <Pagination pagination={pagination} page={page} onChange={setPage} />

      <EmailModal
        open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Listado de Retiros"
        onEnviar={async (email) => {
          const doc = await buildTablePdf('Listado Abonos', columnasPdf, filtradas, opcionesPdf());
          await enviarCorreoAdjunto({
            subject: 'Listado Retiros', email, fileName: 'ListadoPorRetirar.pdf', docPdf: doc,
            textoHtml: plantillaCorreoV1('Adjunto encontraras el listado de Retiros.'),
          });
        }}
      />
      <PorRetirarForm open={formOpen} onClose={() => setFormOpen(false)} socios={[...socios].sort((a, b) => (a.name > b.name ? 1 : -1))} onSubmit={guardar} />
    </div>
  );
}
